import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Calendar, Loader2, Plus, Video } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import API_BASE from '../../utils/apiBase';

const API = API_BASE;

const METHODS = [
  { value: 'video', label: 'Video call' },
  { value: 'phone', label: 'Phone' },
  { value: 'in_person', label: 'In person' },
];

function toLocalInputValue(date) {
  const d = date instanceof Date ? date : new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatDisplay(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function InterviewSchedulingPanel({ employeeId, onUpdated, onRecordInterview, hasApprovedInterview = false }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [markingNoShow, setMarkingNoShow] = useState(false);
  const [newAttempt, setNewAttempt] = useState(false);
  const [appointment, setAppointment] = useState(null);
  const [slots, setSlots] = useState([]);
  const [showSlotForm, setShowSlotForm] = useState(false);
  const [showAnotherInterview, setShowAnotherInterview] = useState(false);
  const [slotForm, setSlotForm] = useState({
    starts_at: toLocalInputValue(new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)),
    method: 'video',
    duration_minutes: 30,
    video_meeting_link: '',
    interviewer_name: '',
    capacity: 1,
  });
  const [manualForm, setManualForm] = useState({
    scheduled_at: toLocalInputValue(new Date(Date.now() + 2 * 24 * 60 * 60 * 1000)),
    method: 'video',
    video_meeting_link: '',
    interviewer_name: '',
  });

  const load = useCallback(async () => {
    if (!token || !employeeId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/employees/${employeeId}/interview-appointment`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAppointment(res.data?.appointment || null);
      setSlots(res.data?.available_slots || []);
    } catch {
      toast.error('Could not load interview scheduling');
    } finally {
      setLoading(false);
    }
  }, [token, employeeId]);

  useEffect(() => { load(); }, [load]);

  const createSlot = async () => {
    if (!token) return;
    setSaving(true);
    try {
      const startsAt = new Date(slotForm.starts_at).toISOString();
      await axios.post(`${API}/interview-slots`, {
        ...slotForm,
        starts_at: startsAt,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Interview slot created');
      setShowSlotForm(false);
      await load();
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create slot');
    } finally {
      setSaving(false);
    }
  };

  const scheduleForApplicant = async () => {
    if (!token || !employeeId) return;
    setSaving(true);
    try {
      const scheduledAt = new Date(manualForm.scheduled_at).toISOString();
      await axios.post(`${API}/employees/${employeeId}/interview-appointment`, {
        ...manualForm,
        scheduled_at: scheduledAt,
        new_attempt: newAttempt,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Interview scheduled for applicant');
      setNewAttempt(false);
      await load();
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not schedule interview');
    } finally {
      setSaving(false);
    }
  };

  const markNoShow = async () => {
    if (!token || !employeeId || !appointment?.id) return;
    setMarkingNoShow(true);
    try {
      await axios.post(`${API}/employees/${employeeId}/interview-appointment/no-show`, {
        appointment_id: appointment.id,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Interview marked as no-show');
      await load();
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not mark no-show');
    } finally {
      setMarkingNoShow(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-600 py-4" data-testid="interview-scheduling-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading interview schedule…
      </div>
    );
  }

  const interviewAlreadyCompleted = hasApprovedInterview || appointment?.status === 'completed';

  return (
    <div className="space-y-4 rounded-xl border border-[#E4E8EB] bg-slate-50/50 p-4" data-testid="interview-scheduling-panel">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <Video className="h-4 w-4 text-primary" />
            Interview scheduling
          </p>
          <p className="text-xs text-slate-600 mt-0.5">
            {hasApprovedInterview
              ? 'Interview decision approved. History remains available; any repeat interview is a separate attempt.'
              : appointment?.status === 'completed'
                ? 'Interview assessment recorded — manager decision still required.'
                : 'Book the interview, then record what happened. Booking alone does not count as completion.'}
          </p>
        </div>
        {(!interviewAlreadyCompleted || showAnotherInterview) && <Button type="button" size="sm" variant="outline" onClick={() => setShowSlotForm((v) => !v)}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add open slot
        </Button>}
      </div>

      {appointment ? (
        <div
          className={`rounded-lg border px-3 py-2 text-sm ${
            appointment.status === 'completed'
              ? 'border-blue-200 bg-blue-50'
              : appointment.status === 'no_show' ? 'border-amber-300 bg-amber-50' : 'border-green-200 bg-green-50'
          }`}
          data-testid="admin-applicant-appointment"
        >
          <p className={`font-medium ${appointment.status === 'completed' ? 'text-blue-900' : appointment.status === 'no_show' ? 'text-amber-950' : 'text-green-900'}`}>
            {appointment.status === 'completed'
              ? hasApprovedInterview ? 'Interview decision approved' : 'Interview conducted — decision pending'
              : appointment.status === 'no_show' ? 'Interview missed — review or reschedule' : 'Applicant appointment'}
          </p>
          <p className={`mt-0.5 ${appointment.status === 'completed' ? 'text-blue-800' : appointment.status === 'no_show' ? 'text-amber-800' : 'text-green-800'}`}>
            {formatDisplay(appointment.scheduled_at)} · {appointment.method_label}
          </p>
          {appointment.video_meeting_link ? (
            <p className={`text-xs mt-1 break-all ${appointment.status === 'completed' ? 'text-blue-700' : appointment.status === 'no_show' ? 'text-amber-800' : 'text-green-700'}`}>
              Link: {appointment.video_meeting_link}
            </p>
          ) : null}
          <div className="mt-2 flex flex-wrap gap-2">
            {appointment.status === 'completed' ? (
              <Badge className="bg-blue-100 text-blue-800">
                {hasApprovedInterview ? 'Interview complete · approved assessment' : 'Assessment recorded · awaiting manager decision'}
              </Badge>
            ) : appointment.status === 'no_show' ? (
              <Badge className="bg-amber-100 text-amber-900">No-show — outcome recorded</Badge>
            ) : ['scheduled', 'confirmed'].includes(String(appointment.status || '').toLowerCase()) ? (
              <Badge className="bg-green-100 text-green-800">
                {appointment.booked_by === 'admin' ? 'Admin scheduled' : 'Applicant booked'}
              </Badge>
            ) : (
              <Badge variant="outline">Booking closed — check history</Badge>
            )}
            {appointment.interviewer_name ? (
              <Badge variant="outline" className="text-slate-700">{appointment.interviewer_name}</Badge>
            ) : null}
            {appointment.is_past && ['scheduled', 'confirmed'].includes(String(appointment.status || '').toLowerCase()) ? (
              <Badge className="bg-amber-100 text-amber-800">Time has passed — awaiting outcome</Badge>
            ) : null}
          </div>
          {appointment.status === 'completed' && !hasApprovedInterview && onRecordInterview ? (
            <Button type="button" size="sm" className="mt-2" onClick={onRecordInterview}
              data-testid="review-interview-assessment">
              Review assessment and record decision
            </Button>
          ) : null}
          {appointment.is_past && ['scheduled', 'confirmed'].includes(String(appointment.status || '').toLowerCase()) ? (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <p className="text-xs text-amber-800">
                This interview time has passed. Record the assessment, reschedule, or mark the applicant as a no-show.
              </p>
              {onRecordInterview && (
                <Button type="button" size="sm" onClick={onRecordInterview}
                  data-testid="record-interview-outcome">
                  Record interview outcome
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                className="border-amber-300 text-amber-800"
                onClick={markNoShow}
                disabled={markingNoShow}
                data-testid="mark-no-show"
              >
                {markingNoShow ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Mark no-show'}
              </Button>
            </div>
          ) : null}
        </div>
      ) : (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          No interview scheduled yet for this applicant.
        </p>
      )}

      {showSlotForm ? (
        <div className="grid gap-3 rounded-lg border border-slate-200 bg-white p-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label className="text-xs">Slot date & time</Label>
            <Input type="datetime-local" value={slotForm.starts_at} onChange={(e) => setSlotForm({ ...slotForm, starts_at: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Method</Label>
            <Select value={slotForm.method} onValueChange={(v) => setSlotForm({ ...slotForm, method: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label className="text-xs">Video meeting link (Teams/Zoom)</Label>
            <Input value={slotForm.video_meeting_link} onChange={(e) => setSlotForm({ ...slotForm, video_meeting_link: e.target.value })} placeholder="https://…" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Interviewer name</Label>
            <Input value={slotForm.interviewer_name} onChange={(e) => setSlotForm({ ...slotForm, interviewer_name: e.target.value })} />
          </div>
          <div className="flex items-end">
            <Button size="sm" onClick={createSlot} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create slot'}
            </Button>
          </div>
        </div>
      ) : null}

      {interviewAlreadyCompleted && !showAnotherInterview && (
        <div className="flex justify-end" data-testid="interview-completed-options">
          <Button type="button" variant="outline" size="sm" onClick={() => {
            setNewAttempt(true);
            setShowAnotherInterview(true);
          }} data-testid="schedule-another-interview">Schedule another interview</Button>
        </div>
      )}
      {(!interviewAlreadyCompleted || showAnotherInterview) && (
      <div className="grid gap-3 rounded-lg border border-slate-200 bg-white p-3 md:grid-cols-2" data-testid="interview-schedule-form">
        <p className="md:col-span-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Schedule this applicant manually</p>
        <div className="space-y-1">
          <Label className="text-xs">Interview date & time</Label>
          <Input type="datetime-local" value={manualForm.scheduled_at} onChange={(e) => setManualForm({ ...manualForm, scheduled_at: e.target.value })} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Method</Label>
          <Select value={manualForm.method} onValueChange={(v) => setManualForm({ ...manualForm, method: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1 md:col-span-2">
          <Label className="text-xs">Video link</Label>
          <Input value={manualForm.video_meeting_link} onChange={(e) => setManualForm({ ...manualForm, video_meeting_link: e.target.value })} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Interviewer</Label>
          <Input value={manualForm.interviewer_name} onChange={(e) => setManualForm({ ...manualForm, interviewer_name: e.target.value })} />
        </div>
        {appointment && ['completed', 'no_show'].includes(String(appointment.status || '').toLowerCase()) ? (
          <label className="md:col-span-2 flex items-center gap-2 text-xs text-slate-700">
            <input
              type="checkbox"
              checked={newAttempt}
              onChange={(e) => setNewAttempt(e.target.checked)}
              data-testid="new-interview-attempt"
            />
            This applicant already has a recorded/closed interview. Tick to deliberately start a <strong>new interview attempt</strong> (previous history is kept).
          </label>
        ) : null}
        <div className="flex items-end gap-2">
          {interviewAlreadyCompleted && <Button type="button" variant="ghost" size="sm" onClick={() => {
            setShowAnotherInterview(false);
            setNewAttempt(false);
          }}>Cancel</Button>}
          <Button size="sm" onClick={scheduleForApplicant} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Schedule applicant'}
          </Button>
        </div>
      </div>
      )}

      {slots.length > 0 && (!interviewAlreadyCompleted || showAnotherInterview) ? (
        <div>
          <p className="text-xs font-medium text-slate-700 mb-2 flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            Open slots ({slots.length})
          </p>
          <ul className="space-y-1 text-xs text-slate-600">
            {slots.slice(0, 5).map((s) => (
              <li key={s.id}>{formatDisplay(s.starts_at)} · {s.spots_remaining} spot(s) left</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}